/**
 * The secondary filter row on Users Management (approval, company, role,
 * status, department). Extracted from UsersManagement to keep files under
 * 500 lines; the state lives in useUsersList.
 */

import { Button } from '../../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { X } from 'lucide-react';
import type { UseUsersListReturn } from './useUsersList';
import { DEPARTMENT_FILTER_OPTIONS } from './usersFilters';
import type { ApprovalFilter, DepartmentFilter, StatusFilter } from './usersFilters';

interface UsersFilterBarProps {
  /** The list state from useUsersList. */
  list: UseUsersListReturn;
}

export default function UsersFilterBar({ list }: UsersFilterBarProps) {
  return (
    <div className="flex flex-wrap gap-2">
      <Select value={list.approvalFilter} onValueChange={(v) => list.setApprovalFilter(v as ApprovalFilter)}>
        <SelectTrigger className="w-[150px]"><SelectValue placeholder="Approval" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Approvals</SelectItem>
          <SelectItem value="approved">Approved</SelectItem>
          <SelectItem value="pending">Pending</SelectItem>
        </SelectContent>
      </Select>
      <Select value={list.companyFilter} onValueChange={list.setCompanyFilter}>
        <SelectTrigger className="w-[200px]"><SelectValue placeholder="Company" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Companies</SelectItem>
          {list.uniqueCompanies.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={list.roleFilter} onValueChange={list.setRoleFilter}>
        <SelectTrigger className="w-[180px]"><SelectValue placeholder="User Role" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All User Roles</SelectItem>
          {list.uniqueRoles.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={list.statusFilter} onValueChange={(v) => list.setStatusFilter(v as StatusFilter)}>
        <SelectTrigger className="w-[140px]"><SelectValue placeholder="All Members" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Members</SelectItem>
          <SelectItem value="active">Active</SelectItem>
          <SelectItem value="invited">Invite Sent</SelectItem>
          <SelectItem value="inactive">Inactive</SelectItem>
        </SelectContent>
      </Select>
      <Select value={list.departmentFilter} onValueChange={(v) => list.setDepartmentFilter(v as DepartmentFilter)}>
        <SelectTrigger className="w-[170px]" aria-label="Department filter">
          <SelectValue placeholder="All departments" />
        </SelectTrigger>
        <SelectContent>
          {DEPARTMENT_FILTER_OPTIONS.map(o => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {list.hasActiveFilters && (
        <Button variant="ghost" size="sm" onClick={list.clearFilters}><X className="h-4 w-4 mr-1" />Clear Filters</Button>
      )}
      <span className="text-sm text-muted-foreground self-center ml-2">
        Showing {list.paginatedUsers.length} of {list.filteredAndSortedUsers.length} users
      </span>
    </div>
  );
}
